import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { trpc } from '@/trpc'
import { useDashboard } from '@/context/DashboardContext'

/**
 * Hook that checks auth on mount via trpc.auth.me and syncs user into DashboardContext.
 * Returns the current dashboard state.
 */
export function useAuth() {
  const { state, dispatch } = useDashboard()
  const queryClient = useQueryClient()
  const currentEmail = state.user?.email
  const currentTenantId = state.user?.badclawTenant?.tenantId
  // HttpOnly cookies also cover existing sessions and OAuth redirects. An
  // absent localStorage marker cannot safely prove that someone is signed out.
  const me = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (me.isError && me.error.data?.code === 'UNAUTHORIZED') {
      // Cached user data can coexist with a rejected refetch. Drop it once;
      // an uncached unauthorized query must not create a refetch/clear loop.
      if (me.data || state.isAuthenticated) queryClient.clear()
      dispatch({ type: 'SET_USER', payload: null })
    } else if (me.data) {
      if (currentEmail !== undefined && (currentEmail !== me.data.email
        || currentTenantId !== me.data.badclawTenant?.tenantId)) {
        queryClient.clear()
      }
      dispatch({
        type: 'SET_USER',
        payload: {
          name: me.data.name,
          email: me.data.email,
          hasSetPassword: (me.data as { hasSetPassword?: boolean }).hasSetPassword ?? false,
          badclawTenant: me.data.badclawTenant
            ? {
                tenantId: me.data.badclawTenant.tenantId,
                containerPort: me.data.badclawTenant.containerPort,
                subdomain: me.data.badclawTenant.subdomain,
                provisionedAt: String(me.data.badclawTenant.provisionedAt),
              }
            : undefined,
        },
      })
    } else if (me.isError) {
      // A network/server outage is not proof of logout. Preserve any current
      // identity and only unblock the auth gate.
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [me.data, me.error, me.isError, state.isAuthenticated, currentEmail, currentTenantId, queryClient, dispatch])

  return state
}
